import { useState, useEffect, useCallback } from 'react'
import { adminPushCondicionService } from '../../services/adminPushCondicionService'
import { usuarioService } from '../../services/usuarioService'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Input from '../ui/Input'
import LoadingState from '../ui/LoadingState'
import EmptyState from '../ui/EmptyState'
import { Bell, Search, RefreshCw, Send, AlertCircle, CheckCircle } from 'lucide-react'

export default function PushCondicionSaludPanel() {
  const [searchCond, setSearchCond] = useState('')
  const [condPage, setCondPage] = useState(1)
  const [condLoading, setCondLoading] = useState(false)
  const [condData, setCondData] = useState(null)
  const [selectedCond, setSelectedCond] = useState(null)

  const [searchUser, setSearchUser] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)

  const [titulo, setTitulo] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const loadCondiciones = useCallback(async (page = 1) => {
    try {
      setCondLoading(true)
      setError(null)
      const resp = await adminPushCondicionService.mascotaCondiciones({
        page,
        per_page: 12,
        ...(searchCond.trim() ? { search: searchCond.trim() } : {}),
      })
      if (resp?.success) {
        setCondData(resp.data)
        setCondPage(page)
      } else {
        setError(resp?.error || 'No se pudieron cargar las condiciones en mascotas')
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al cargar')
    } finally {
      setCondLoading(false)
    }
  }, [searchCond])

  useEffect(() => {
    const t = setTimeout(() => loadCondiciones(1), searchCond ? 400 : 0)
    return () => clearTimeout(t)
  }, [searchCond, loadCondiciones])

  const loadUsuarios = async () => {
    try {
      setUsersLoading(true)
      setError(null)
      const resp = await usuarioService.getAll({
        per_page: 30,
        ...(searchUser.trim() ? { search: searchUser.trim() } : {}),
      })
      if (resp?.success) {
        const list = resp.data?.data || resp.data || []
        setUsers(Array.isArray(list) ? list : [])
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al cargar usuarios')
    } finally {
      setUsersLoading(false)
    }
  }

  useEffect(() => {
    loadUsuarios()
  }, [])

  useEffect(() => {
    const t = setTimeout(() => loadUsuarios(), 400)
    return () => clearTimeout(t)
  }, [searchUser])

  const handleEnviar = async () => {
    const idCond = selectedCond?.id_mascota_condicion ?? selectedCond?.idMascotaCondicion
    const idUser = selectedUser?.id_usuario ?? selectedUser?.idUsuario
    if (!idCond || !idUser) {
      setError('Selecciona una condición en mascota y un usuario destino.')
      return
    }
    try {
      setSending(true)
      setError(null)
      setSuccess(null)
      const payload = {
        id_mascota_condicion: idCond,
        id_usuario_destino: idUser,
        ...(titulo.trim() ? { titulo: titulo.trim() } : {}),
        ...(mensaje.trim() ? { mensaje: mensaje.trim() } : {}),
      }
      const resp = await adminPushCondicionService.enviar(payload)
      if (resp?.success) {
        setSuccess(resp.message || 'Notificación enviada.')
      } else {
        setError(resp?.error || 'No se pudo enviar')
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al enviar')
    } finally {
      setSending(false)
    }
  }

  const rows = condData?.data || []
  const lastPage = condData?.last_page || 1

  return (
    <div className="space-y-6 mt-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Push de condición de salud (manual)
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Elige una condición activa en una mascota y un usuario destino. Si dejas título y mensaje vacíos, se usa el mismo texto
            automático que el cron (días sin seguimiento en ficha o diagnóstico).
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-5 h-5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-800 dark:text-green-200">
              <CheckCircle className="w-5 h-5 shrink-0" />
              {success}
            </div>
          )}

          <div>
            <div className="flex flex-wrap gap-2 items-end mb-3">
              <div className="flex-1 min-w-[200px]">
                <Input
                  label="Buscar condición en mascotas"
                  placeholder="Nombre mascota, condición o ID numérico"
                  value={searchCond}
                  onChange={(e) => setSearchCond(e.target.value)}
                  leftIcon={Search}
                />
              </div>
              <Button type="button" variant="secondary" onClick={() => loadCondiciones(condPage)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
            {condLoading ? (
              <LoadingState message="Cargando..." />
            ) : rows.length === 0 ? (
              <EmptyState icon={Bell} title="Sin resultados" description="No hay condiciones que coincidan." />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left">ID</th>
                      <th className="px-3 py-2 text-left">Mascota</th>
                      <th className="px-3 py-2 text-left">Condición</th>
                      <th className="px-3 py-2 text-left" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const id = row.id_mascota_condicion ?? row.idMascotaCondicion
                      const sel =
                        (selectedCond?.id_mascota_condicion ?? selectedCond?.idMascotaCondicion) === id
                      return (
                        <tr
                          key={id}
                          className={sel ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{id}</td>
                          <td className="px-3 py-2">{row.mascota?.nombre || '—'}</td>
                          <td className="px-3 py-2">{row.condicion_salud?.nombre || row.condicionSalud?.nombre || '—'}</td>
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant={sel ? 'primary' : 'secondary'} onClick={() => setSelectedCond(row)}>
                              {sel ? 'Seleccionada' : 'Elegir'}
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {condData && lastPage > 1 && (
              <div className="flex justify-between items-center mt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={condPage <= 1 || condLoading}
                  onClick={() => loadCondiciones(condPage - 1)}
                >
                  Anterior
                </Button>
                <span className="text-xs text-gray-500">
                  Página {condPage} / {lastPage}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={condPage >= lastPage || condLoading}
                  onClick={() => loadCondiciones(condPage + 1)}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </div>

          <div>
            <Input
              label="Buscar usuario destino"
              placeholder="Nombre, email..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              leftIcon={Search}
            />
            {usersLoading ? (
              <LoadingState message="Cargando usuarios..." />
            ) : (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                {users.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">Sin usuarios</p>
                ) : (
                  users.map((u) => {
                    const id = u.id_usuario ?? u.idUsuario
                    const sel = (selectedUser?.id_usuario ?? selectedUser?.idUsuario) === id
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setSelectedUser(u)}
                        className={`w-full text-left px-3 py-2 text-sm flex justify-between gap-2 ${
                          sel ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        }`}
                      >
                        <span>
                          {u.nombre} {u.apellido || ''} · {u.email}
                        </span>
                        <span className="text-xs text-gray-500 shrink-0">{u.tipo_usuario || u.tipoUsuario}</span>
                      </button>
                    )
                  })
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Título (opcional)"
              placeholder="Por defecto: Seguimiento de condición de salud"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Mensaje (opcional)
              </label>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                rows={4}
                placeholder="Vacío = mensaje automático con días sin seguimiento o desde diagnóstico"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleEnviar} loading={sending} disabled={sending}>
              <Send className="w-4 h-4 mr-2" />
              Enviar push
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
